import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Customer, OrderService, ListCustomerOrdersRequest, ListCustomerOrdersResponse } from '../api/api_pb';
import RelativeTime from '../components/RelativeTime';
import { useGlobalContext } from '../GlobalContext';
import { useClient } from '../connect_api';
import SMSDeliveryLogs from '../components/SMSDeliveryLogs';
import SendSMSModal from '../components/SendSMSModal';
import MobileNumberActions from '../components/MobileNumberActions';
import { MessageSquare } from 'lucide-react';

const CustomerDetail: React.FC = () => {
    const { customerUuid } = useParams<{ customerUuid: string }>();
    const navigate = useNavigate();
    const { customers, updateCustomers } = useGlobalContext();
    const orderClient = useClient(OrderService);
    const [customer, setCustomer] = useState<Customer | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showSMSModal, setShowSMSModal] = useState(false);

    const fetchCustomerFromOrders = useCallback(async () => {
        if (!customerUuid) return;
        try {
            const request = { customerUuid } as ListCustomerOrdersRequest;
            const response = await orderClient.listCustomerOrders(request);
            const { orders } = response as ListCustomerOrdersResponse;
            if (orders.length > 0 && orders[0].customer) {
                const fetchedCustomer = orders[0].customer;
                setCustomer(fetchedCustomer);
                updateCustomers(fetchedCustomer);
            }
        } catch (error) {
            console.error('Failed to fetch customer from orders:', error);
        } finally {
            setIsLoading(false);
        }
    }, [customerUuid, orderClient, updateCustomers]);

    useEffect(() => {
        if (customerUuid && customers) {
            const foundCustomer = customers.find(c => c.uuid === customerUuid);
            if (foundCustomer) {
                setCustomer(foundCustomer);
                setIsLoading(false);
                return;
            }
        }
        fetchCustomerFromOrders();
    }, [customerUuid, customers, fetchCustomerFromOrders]);

    const handleShowLeases = () => {
        if (customer) {
            navigate(`/show-lease/${customer.uuid}`);
        }
    };

    const handleBack = () => {
        navigate('/customers');
    };

    if (isLoading) {
        return (
            <div className="p-4">
                <div className="text-center">Loading customer...</div>
            </div>
        );
    }

    if (!customer) {
        return (
            <div className="p-4">
                <button
                    onClick={handleBack}
                    className="mb-4 bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                >
                    Back to Customers
                </button>
                <div className="text-center">Customer not found</div>
            </div>
        );
    }

    return (
        <div className="p-4">
            <div className="mb-4">
                <button 
                    onClick={handleBack}
                    className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                >
                    Back to Customers
                </button>
            </div>

            <div className="bg-white shadow rounded-lg p-6 mb-6">
                <h2 className="text-2xl font-bold mb-4">Customer Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="font-semibold">Mobile Number:</label>
                        <p>
                            <MobileNumberActions
                                mobileNumber={customer.mobileNumber}
                                customerUuid={customer.uuid}
                                onSMSClick={() => setShowSMSModal(true)}
                            />
                        </p>
                    </div>
                    <div>
                        <label className="font-semibold">Customer ID:</label>
                        <p className="text-sm text-gray-600">{customer.uuid}</p>
                    </div>
                    <div>
                        <label className="font-semibold">Created At:</label>
                        <p>
                            {customer.createdAt ? (
                                <div className="text-sm">
                                    <div><RelativeTime timestamp={customer.createdAt} /></div>
                                </div>
                            ) : 'N/A'}
                        </p>
                    </div>
                </div>
                <div className="mt-4 flex gap-2">
                    <button
                        onClick={handleShowLeases}
                        className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                    >
                        Show Leases
                    </button>
                    <button
                        onClick={() => setShowSMSModal(true)}
                        className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700 flex items-center gap-2"
                    >
                        <MessageSquare className="w-4 h-4" />
                        SMS Customer
                    </button>
                </div>
            </div>

            <div className="bg-white shadow rounded-lg p-6">
                <h3 className="text-xl font-bold mb-4">SMS Delivery History</h3>
                <p className="text-sm text-gray-600 mb-4">SMS messages sent to this customer with delivery status</p>
                <SMSDeliveryLogs customerId={customer.uuid} />
            </div>

            <SendSMSModal
                isOpen={showSMSModal}
                onClose={() => setShowSMSModal(false)}
                customerUuid={customer.uuid}
                mobileNumber={customer.mobileNumber}
            />
        </div>
    );
};

export default CustomerDetail;
